/* eslint-disable react/jsx-curly-newline */
/* eslint-disable react/jsx-wrap-multilines */
/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import PropTypes from 'prop-types';
import {
  Button,
  ButtonIcon,
  ButtonWithMenu,
  Icon,
  MenuItem,
  MenuItemText,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  SearchField,
  Tooltip,
  Typography,
} from '@statsbomb/kitbag-components';
import { isEmpty, orderBy } from 'lodash';
import { useKitbagAuth } from '@statsbomb/kitbag-auth';
import { mf_Leagues } from '../../../../apollo';
import SortableTable from '../../../../components/Tables/SortableTable/SortableTable';
import {
  LoadModalContainer,
  LoadModalHeader,
  LoadModalSearchWrapper,
  LoadModalStatusWrapper,
  LoadModalTableWrapper,
} from './LoadModal.styles';
import { formatScoutLoadTemplateTable } from './LoadModal.dataManipulation';
import { SORT_DIRECTIONS } from '../../../../utils/constants/sortDirections';
import {
  API_COMPETITION,
  API_STAT_UNITS,
} from '../../../../utils/constants/api';
import {
  ButtonWithMenuWrapper,
  IconWarningColour,
  TableHeadWrapper,
} from '../../../../components/Tables/SortableTable/SortableTable.styles';
import { SCOUT_LOAD_MODAL_HEADERS } from './LoadModal.constants';
import { SCOUT_TEMPLATE_MODES } from '../../Scout.constants';
import {
  trackContentSearchEvent,
  trackObjectOpenCloseEvent,
} from '../../../../utils/helpers/snowplow';
import { useStore } from '../../../../store/useStore';

const LoadModal = ({
  modalId,
  handleMenuAction,
  __storybookScoutSetups = null,
  __storybookLoadModalOpen = null,
}) => {
  const theme = useTheme();
  const {
    activeTemplateId,
    loadTemplate,
    savedScoutTemplates,
    loadModalOpen,
    setLoadModalOpen,
  } = useStore();
  // user
  const { user } = useKitbagAuth();
  const [isOpen, setIsOpen] = useState(loadModalOpen ?? false);
  // templates saved to the account
  const savedSetups = __storybookScoutSetups || savedScoutTemplates;
  // table vars
  const [searchValue, setSearchValue] = useState('');
  const { ASCENDING, DESCENDING } = SORT_DIRECTIONS;
  const [sortDirection, setSortDirection] = useState(DESCENDING);
  const [sortKey, setSortKey] = useState('lastModified');
  const [selectedRow, setSelectedRow] = useState(null);
  const [openMenuRow, setOpenMenuRow] = useState(null);

  // format the table rows
  const scoutTemplatesRows = formatScoutLoadTemplateTable(
    savedSetups,
    SCOUT_LOAD_MODAL_HEADERS
  );

  // sort formatted rows
  const sortedScoutTemplatesRows = orderBy(
    scoutTemplatesRows,
    [`${sortKey}.value`],
    [sortDirection]
  );

  // Filter rows by search value
  const getFilteredRows = () => {
    if (!searchValue) return sortedScoutTemplatesRows;
    const regex = new RegExp(searchValue, 'i');
    return sortedScoutTemplatesRows.filter((row) =>
      regex.test(row.name?.label || '')
    );
  };
  const showResults = !isEmpty(getFilteredRows());
  const hasSavedSetups = !isEmpty(savedSetups);

  // sort table columns
  const handleSort = (statName) => {
    if (statName === sortKey) {
      setSortDirection(sortDirection === ASCENDING ? DESCENDING : ASCENDING);
    } else {
      const sortColumn = scoutTemplatesRows.find((h) => h.key === statName);
      const defaultSortDirection =
        sortColumn?.units === API_STAT_UNITS.STRING ? ASCENDING : DESCENDING;
      setSortDirection(defaultSortDirection);
      setSortKey(statName);
    }
  };

  // Analytics tracking
  const isFirstRender = useRef(true);
  const prevIsOpen = useRef(isOpen);

  useLayoutEffect(() => {
    isFirstRender.current = false;
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      prevIsOpen.current = isOpen;
      return;
    }
    if (prevIsOpen.current !== isOpen) {
      trackObjectOpenCloseEvent(
        'List View',
        isOpen,
        'Scout Load Filter Sets Modal'
      );
    }
    prevIsOpen.current = isOpen;
  }, [isOpen]);

  const isNFL = API_COMPETITION[mf_Leagues()] === 'NFL';

  return (
    <>
      <Button
        size="small"
        variant="secondary"
        onClick={() => {
          setIsOpen(true);
          setSelectedRow(activeTemplateId);
        }}
        onClose={() => setIsOpen(false)}
      >
        Load
      </Button>

      <Modal
        id={modalId}
        isOpen={__storybookLoadModalOpen || isOpen}
        onCancel={() => setIsOpen(false)}
        onEscape={() => setIsOpen(false)}
      >
        <ModalHeader>
          <LoadModalHeader>
            <div>Filter Sets</div>
            <div style={{ position: 'absolute', right: '1rem', top: '1rem' }}>
              <ButtonIcon
                onClick={() => {
                  setIsOpen(false);
                }}
                variant="ghost"
                shape="pill"
                size="small"
                icon="Close"
                title="Cancel"
              />
            </div>

            <LoadModalSearchWrapper>
              <SearchField
                value={searchValue}
                onChange={(e) => {
                  trackContentSearchEvent(e.target.value, 'Search bar');
                  setSearchValue(e.target.value);
                }}
                onClear={() => setSearchValue('')}
                placeholder="Search for a filter set"
                searchIconTitle="search"
              />
            </LoadModalSearchWrapper>
          </LoadModalHeader>
        </ModalHeader>
        <ModalBody>
          <LoadModalContainer>
            {showResults && (
              <LoadModalTableWrapper>
                <TableHeadWrapper>
                  <SortableTable
                    name="load-scout-templates-table"
                    rows={getFilteredRows()}
                    headers={SCOUT_LOAD_MODAL_HEADERS}
                    handleSort={handleSort}
                    sortDirection={sortDirection}
                    sortBy={sortKey}
                    border="0"
                    rowHeight="2rem"
                    showExpanding={false}
                    onRowClick={(row) => {
                      setSelectedRow(
                        row?.id.label !== selectedRow ? row.id.label : null
                      );
                      setOpenMenuRow(null);
                    }}
                    renderMetricCountCell={(cell) =>
                      isNFL && cell.hasNcaaTransferPortal ? (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.sizing.tiny,
                          }}
                        >
                          {cell?.label}
                          <Tooltip
                            content={
                              <div style={{ width: '210px' }}>
                                {`1/${cell?.label} filters can not be applied:`}
                                <br />
                                Only players in transfer portal = true
                              </div>
                            }
                            placement="bottom-centre"
                          >
                            <IconWarningColour>
                              <Icon variant="Info" size="small" />
                            </IconWarningColour>
                          </Tooltip>
                        </div>
                      ) : (
                        cell?.label
                      )
                    }
                    renderActionsCell={(rowIndex, rows) => (
                      <ButtonWithMenu
                        icon="More"
                        onClick={(event) => {
                          event.stopPropagation();
                          setOpenMenuRow(
                            openMenuRow === rowIndex ? null : rowIndex
                          );
                        }}
                        onOutsideClick={(event) => {
                          event.stopPropagation();
                          setOpenMenuRow(null);
                        }}
                        placement="bottom-end"
                        variant="ghost"
                        size="small"
                        isOpen={openMenuRow === rowIndex}
                        title="more"
                      >
                        <ButtonWithMenuWrapper>
                          <MenuItem
                            onClick={(event) => {
                              event.stopPropagation();
                              handleMenuAction(
                                SCOUT_TEMPLATE_MODES.EDIT,
                                rows[rowIndex]
                              );
                            }}
                            disabled={
                              rows[rowIndex].creatorId.label !== user?.sub
                            }
                          >
                            <MenuItemText>Rename</MenuItemText>
                            <Icon variant="Edit" size="small" />
                          </MenuItem>
                          <MenuItem
                            onClick={(event) => {
                              event.stopPropagation();
                              handleMenuAction(
                                SCOUT_TEMPLATE_MODES.DELETE,
                                rows[rowIndex]
                              );
                            }}
                            disabled={
                              rows[rowIndex].creatorId.label !== user?.sub
                            }
                          >
                            <MenuItemText>Delete</MenuItemText>
                            <Icon variant="Delete" size="small" />
                          </MenuItem>
                          <MenuItem
                            onClick={(event) => {
                              event.stopPropagation();
                              handleMenuAction(
                                SCOUT_TEMPLATE_MODES.SAVE_AS,
                                rows[rowIndex]
                              );
                            }}
                          >
                            <MenuItemText>Save as New</MenuItemText>
                            <Icon variant="SaveAs" size="small" />
                          </MenuItem>
                        </ButtonWithMenuWrapper>
                      </ButtonWithMenu>
                    )}
                  />
                </TableHeadWrapper>
              </LoadModalTableWrapper>
            )}
            {!showResults && (
              <LoadModalStatusWrapper>
                <Icon variant="Info" size="regular" />
                <Typography variant="headingLarge" data-testId="load-header">
                  No Filter Sets
                </Typography>
                <Typography
                  variant="bodyRegular"
                  data-testId="load-error-message"
                >
                  {hasSavedSetups
                    ? 'No filter sets match the search criteria'
                    : 'Save a filter set to see it here'}
                </Typography>
              </LoadModalStatusWrapper>
            )}
          </LoadModalContainer>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="tertiary"
            onClick={() => {
              setIsOpen(false);
              setLoadModalOpen(false);
              setSelectedRow(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              const selectedSetup = savedSetups.find(
                (s) => s.id === selectedRow
              );
              loadTemplate(selectedSetup);
              setSelectedRow(null);
              setIsOpen(false);
              setLoadModalOpen(false);
            }}
            isDisabled={selectedRow === null}
            testId="load-filter-set"
          >
            Load
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};
LoadModal.propTypes = {
  modalId: PropTypes.string.isRequired,
  handleMenuAction: PropTypes.func.isRequired,
  __storybookScoutSetups: PropTypes.arrayOf(PropTypes.object),
  __storybookLoadModalOpen: PropTypes.bool,
};

export default LoadModal;
